import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import CampoTexto from '@/components/base/CampoTexto';

interface Props {
  eventoId: number;
  pontoId: number;
  aoFechar: () => void;
  aoSalvo: () => void;
}

export default function ModalConvidar({ eventoId, pontoId, aoFechar, aoSalvo }: Props) {
  const [email, setEmail] = useState('');
  const [podeCaixa, setPodeCaixa] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    const valor = email.trim().toLowerCase();
    if (!valor || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor)) {
      setErro('Digite um e-mail válido.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('loja_convidar_operador', {
        p_evento: eventoId,
        p_email: valor,
        p_ponto: pontoId,
        p_caixa: podeCaixa,
      });
      if (error) throw error;
      aoSalvo();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={aoFechar} aria-hidden />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-[480px] flex-col overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex flex-col items-center pt-2 sm:hidden">
          <span className="h-1.5 w-10 rounded-full bg-secondary-200" />
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <h3 className="text-[17px] font-bold text-foreground-950">Convidar por e-mail</h3>
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          <CampoTexto
            id="loja-email-operador"
            name="email"
            label="E-mail"
            type="email"
            inputMode="email"
            value={email}
            onChange={setEmail}
            required
            error={erro}
            autoFocus
            autoComplete="email"
            placeholder="ex.: maria@email.com"
          />

          <div className="mt-5 flex items-start justify-between gap-3">
            <div>
              <p className="text-[15px] font-medium text-foreground-950">Pode vender no caixa</p>
              <p className="mt-1 text-[13px] text-foreground-500">
                Lança vendas em dinheiro, Pix e cartão na tela do caixa.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={podeCaixa}
              onClick={() => setPodeCaixa((v) => !v)}
              className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors ${
                podeCaixa ? 'bg-primary-500' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                  podeCaixa ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <p className="mt-4 text-[13px] leading-relaxed text-foreground-500">
            Quando essa pessoa entrar na plataforma com este e-mail, ela já vira operadora — sem
            tela de aceitar. Ela precisa ter (ou criar) uma conta com este e-mail.
          </p>
        </div>

        <div className="border-t border-background-100 px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={salvar}
            disabled={salvando}
            className="flex min-h-[52px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 text-[15px] font-bold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {salvando ? 'Convidando…' : 'Convidar'}
          </button>
          <button
            type="button"
            onClick={aoFechar}
            className="mx-auto mt-2 block cursor-pointer whitespace-nowrap text-[14px] font-medium text-foreground-500 transition-colors hover:text-foreground-800"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}